import * as React from 'react';
import { styled } from '@linaria/react';

import useTickSfx from '@/hooks/use-tick-sfx';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';

import {
  Svg,
  CodeSnippet,
  Row,
  Label,
  PositionDot,
} from './BasicShapes.shared';
import GuideLine from './GuideLine';

const SIZE = 280;

function BasicTextDemo() {
  const playTick = useTickSfx();

  return (
    <Demo
      id="basic-text-demo"
      initialValues={{
        x: Math.round(SIZE / 2),
        y: Math.round(SIZE / 2),
        fontSize: 32,
      }}
      outputWrapper={OutputWrapper}
      CustomGridComponent={ControlsGrid}
      controls={[
        <DemoSliderControl
          highlightControlWhenActive
          key="x"
          id="x"
          label="x"
          min={0}
          max={SIZE}
          step={10}
          wrapperStyle={{ gridArea: 'x' }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="y"
          id="y"
          label="y"
          min={0}
          max={SIZE}
          step={10}
          wrapperStyle={{ gridArea: 'y' }}
          onChange={() => {
            playTick();
          }}
        />,
      ]}
    >
      {({ x, y }, activeControlId) => {
        const code = `<svg width="${SIZE}" height="${SIZE}">
  <text
    x="${x}"
    y="${y}"
    text-anchor="middle"
    dominant-baseline="middle"
    fill="oklch(0.9 0.3 164)"
  >
    Hello World
  </text>
</svg>`;

        return (
          <StyledRow>
            <StyledCodeSnippet code={code} />
            <SvgWrapper>
              <Svg size={SIZE}>
                <GuideLine
                  x1={x}
                  y1={y}
                  x2={0}
                  y2={y}
                  isActive={activeControlId === 'x'}
                />
                <GuideLine
                  x1={x}
                  y1={y}
                  x2={x}
                  y2={0}
                  isActive={activeControlId === 'y'}
                />
                <Label
                  x={x / 2}
                  y={y + 24}
                  data-is-active={activeControlId === 'x'}
                  data-is-hidden={x < 40}
                >
                  x
                </Label>
                <Label
                  x={x + 24}
                  y={y / 2}
                  data-is-active={activeControlId === 'y'}
                  data-is-hidden={y < 50}
                >
                  y
                </Label>
                <StyledText
                  x={x}
                  y={y}
                  fontSize="2rem"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  stroke="var(--color-background)"
                  strokeWidth={2}
                  fill="var(--wham-complementary-500)"
                >
                  Hello World
                </StyledText>
                <StyledText
                  x={x}
                  y={y}
                  fontSize="2rem"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="var(--wham-complementary-500)"
                >
                  Hello World
                </StyledText>
                <PositionDot cx={x} cy={y} />
              </Svg>
            </SvgWrapper>
          </StyledRow>
        );
      }}
    </Demo>
  );
}

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;

const ControlsGrid = styled.div`
  display: grid;
  gap: 16px 32px;
  grid-template-areas:
    'x'
    'y';

  @media (min-width: 30rem) {
    grid-template-areas: 'x y';
  }
`;

const StyledRow = styled(Row)`
  @container (max-width: 37rem) {
    justify-content: center;
  }
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  @container (max-width: 37rem) {
    display: none;
  }
`;

const SvgWrapper = styled.div`
  display: flex;

  @container (max-width: 37rem) {
    position: relative;
  }
`;

const StyledText = styled.text`
  font-family: var(--font-family);
  font-weight: bold;
`;

export default BasicTextDemo;
